import React, { useState, useEffect, useRef } from 'react';
import {
    Building, Printer, CreditCard, User, Save, Moon, Sun, CheckCircle2,
    ImagePlus, X, Sparkles, ExternalLink, Eye, EyeOff, Loader2, Plug, Wifi, WifiOff,
    Activity, AlertTriangle, Copy, Key, FileText, Globe, Plus, Trash2, Download, Upload, Edit3, MapPin, Navigation, Database, Cloud, Tag,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { cn } from '../../utils/theme';
import { useBusinessConfig } from '../../hooks/useBusinessConfig';
import { useRole } from '../../hooks/useRole';
import { api } from '../../utils/api';
import { getAISettings, saveAISettings, testAIConnection } from '../../services/AIService';
import { getPrinterConfig, savePrinterConfig } from '../../hooks/usePrinterConfig';
import { usePrinterNetworkConfig, invalidatePrinterConfigCache } from '../../hooks/usePrinterNetworkConfig';
import { BackupManager } from '../../components/BackupManager/BackupManager';
import { CloudSyncManager } from '../../components/CloudSyncManager/CloudSyncManager';
import { PromotionsManager } from '../../components/PromotionsManager';

// ⚠️ Definido FUERA del componente: si se define dentro, React lo recrea en cada
// render y el input pierde el foco al escribir (no se puede teclear normal).
function Field({ label, id, value, onChange, placeholder = '', multiline = false }: {
    label: string; id: string; value: string; onChange: (v: string) => void;
    placeholder?: string; multiline?: boolean;
}) {
    return (
        <div>
            <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {label}
            </label>
            {multiline ? (
                <textarea
                    id={id}
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    placeholder={placeholder}
                    rows={3}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                />
            ) : (
                <input
                    id={id}
                    type="text"
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    placeholder={placeholder}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
            )}
        </div>
    );
}

/**
 * Muestra el token de autenticación actual (el mismo que usa el navegador)
 * para copiarlo al .env del Print Bridge fácilmente.
 */
function BridgeTokenBox() {
    const [visible, setVisible] = useState(false);
    const [copied, setCopied] = useState(false);
    const token = localStorage.getItem('authToken') || '';

    const handleCopy = () => {
        if (!token) return;
        navigator.clipboard.writeText(token).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <div className="rounded-xl border border-blue-100 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/30 p-4 space-y-2">
            <p className="text-sm font-semibold flex items-center gap-2 text-blue-800 dark:text-blue-300">
                <Key size={15} />
                Token de API para el Print Bridge
            </p>
            <p className="text-xs text-blue-600 dark:text-blue-400">
                Copia este valor en el campo{' '}
                <code className="bg-blue-100 dark:bg-blue-900 px-1 py-0.5 rounded font-mono">API_TOKEN</code>{' '}
                del archivo <code className="bg-blue-100 dark:bg-blue-900 px-1 py-0.5 rounded font-mono">.env</code> del bridge.
            </p>
            {token ? (
                <div className="flex items-center gap-2">
                    <input
                        id="bridge-api-token"
                        type={visible ? 'text' : 'password'}
                        readOnly
                        value={token}
                        className="flex-1 px-3 py-2 rounded-lg border border-blue-200 dark:border-blue-800 bg-white dark:bg-neutral-800 text-sm font-mono text-gray-800 dark:text-gray-200 focus:outline-none cursor-text"
                        onFocus={e => e.target.select()}
                    />
                    <button
                        type="button"
                        onClick={() => setVisible(v => !v)}
                        title={visible ? 'Ocultar' : 'Mostrar'}
                        className="p-2 rounded-lg border border-blue-200 dark:border-blue-800 bg-white dark:bg-neutral-800 text-gray-500 hover:text-blue-600 transition"
                    >
                        {visible ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                    <button
                        type="button"
                        onClick={handleCopy}
                        title="Copiar token"
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition ${
                            copied
                                ? 'bg-emerald-500 text-white'
                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}
                    >
                        {copied ? <CheckCircle2 size={15} /> : <Copy size={15} />}
                        {copied ? '¡Copiado!' : 'Copiar'}
                    </button>
                </div>
            ) : (
                <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                    <AlertTriangle size={12} />
                    No se encontró un token. Asegúrate de estar logueado.
                </p>
            )}
        </div>
    );
}

export default function Settings() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { isAdmin } = useRole();
    const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'general');
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        const tab = searchParams.get('tab');
        if (tab && tab !== activeTab) {
            setActiveTab(tab);
        }
    }, [searchParams]);

    // Estado Factura Electrónica DIAN
    const [dianForm, setDianForm] = useState({
        enabled: false,
        api_url: 'https://api-sandbox.factus.co',
        api_token: '',
        simulation: true,
        default_tax: 'EXENTO',
        resolution_number: '',
        resolution_prefix: 'SETT',
        resolution_start: '1',
        resolution_end: '500000',
        resolution_date: '',
        issuer_nit: '',
        issuer_dv: '',
        issuer_name: '',
        issuer_regime: 'O-99',
        issuer_address: '',
        issuer_city_code: '11001',
    });
    const [loadingDian, setLoadingDian] = useState(false);
    const [savingDian, setSavingDian] = useState(false);

    useEffect(() => {
        if (activeTab === 'dian') {
            setLoadingDian(true);
            api.get('/dian/config')
                .then(res => {
                    setDianForm(res.data);
                })
                .catch(() => {
                    toast.error('No se pudo cargar la configuración DIAN');
                })
                .finally(() => setLoadingDian(false));
        }
    }, [activeTab]);

    const handleSaveDian = async (e: React.FormEvent) => {
        e.preventDefault();
        setSavingDian(true);
        try {
            await api.post('/dian/config', dianForm);
            toast.success('Configuración DIAN guardada con éxito');
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'Error al guardar la configuración DIAN');
        } finally {
            setSavingDian(false);
        }
    };

    const [darkMode, setDarkMode] = useState(() =>
        localStorage.getItem('theme') === 'dark' ||
        document.documentElement.classList.contains('dark')
    );
    const [paperWidth, setPaperWidth] = useState<number>(() => getPrinterConfig().paperWidth);
    const { config: printerNetCfg, saving: savingPrinter, save: savePrinterNet, test: testPrinter } = usePrinterNetworkConfig();
    const [printerIp, setPrinterIp] = useState<string>('');
    const [printerPort, setPrinterPort] = useState<string>('9100');
    const [testingPrinter, setTestingPrinter] = useState(false);
    const [printerTestResult, setPrinterTestResult] = useState<{ ok: boolean; message: string } | null>(null);

    // Estado del bridge local
    const [bridgeStatus, setBridgeStatus] = useState<{
        pending: number;
        printing: number;
        failed_last_24h: number;
        last_printed_at: string | null;
        bridge_active: boolean;
    } | null>(null);
    const bridgePollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        const fetchBridgeStatus = async () => {
            try {
                const res = await api.get<typeof bridgeStatus>('/print-jobs/status');
                setBridgeStatus(res.data);
            } catch { /* silencioso */ }
        };
        if (activeTab === 'hardware') {
            fetchBridgeStatus();
            bridgePollRef.current = setInterval(fetchBridgeStatus, 5000);
        }
        return () => { if (bridgePollRef.current) clearInterval(bridgePollRef.current); };
    }, [activeTab]);

    // Sync local state when network config loads from backend
    useEffect(() => {
        if (printerNetCfg.ip) {
            setPrinterIp(printerNetCfg.ip);
            setPrinterPort(String(printerNetCfg.port));
        }
    }, [printerNetCfg.ip, printerNetCfg.port]);

    const { config, save } = useBusinessConfig();
    const [form, setForm] = useState(config);

    // Sync form with loaded config from backend
    useEffect(() => {
        setForm(config);
    }, [config]);

    const set = (key: keyof typeof form, value: any) =>
        setForm(prev => ({ ...prev, [key]: value }));

    const toggleTheme = () => {
        const isDark = !darkMode;
        setDarkMode(isDark);
        document.documentElement.classList.toggle('dark', isDark);
        localStorage.setItem('theme', isDark ? 'dark' : 'light');
    };

    const handleSaveGeneral = async () => {
        try {
            await save(form);
            setSaved(true);
            toast.success('Configuración guardada en el servidor');
            setTimeout(() => setSaved(false), 2500);
        } catch (err) {
            console.error(err);
            toast.error('Error al guardar la configuración en el servidor. Revisa tu conexión.');
        }
    };

    // Logo upload → data URL
    const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = ev => set('logoUrl', ev.target?.result as string);
        reader.readAsDataURL(file);
    };

    // ── AI providers ─────────────────────────────────────────────────────────
    const qc = useQueryClient();
    const { data: aiConfig } = useQuery({
        queryKey: ['ai-settings'],
        queryFn: getAISettings,
    });

    const [selectedProvider, setSelectedProvider] = useState<string>('');
    const [apiKeyInput, setApiKeyInput] = useState('');
    const [modelInput, setModelInput] = useState('');
    const [showKey, setShowKey] = useState(false);
    const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null);

    // Sincronizar selección con el proveedor activo al cargar
    useEffect(() => {
        if (aiConfig && !selectedProvider) {
            setSelectedProvider(aiConfig.active);
            const active = aiConfig.providers.find(p => p.id === aiConfig.active);
            setModelInput(active?.model ?? '');
        }
    }, [aiConfig, selectedProvider]);

    const selectedInfo = aiConfig?.providers.find(p => p.id === selectedProvider);

    const saveAiMutation = useMutation({
        mutationFn: () => saveAISettings({
            provider: selectedProvider,
            api_key: apiKeyInput || undefined,
            model: modelInput || undefined,
        }),
        onSuccess: () => {
            toast.success('Configuración de IA guardada');
            setApiKeyInput('');
            setTestResult(null);
            qc.invalidateQueries({ queryKey: ['ai-settings'] });
        },
        onError: () => toast.error('No se pudo guardar la configuración'),
    });

    const testAiMutation = useMutation({
        mutationFn: testAIConnection,
        onSuccess: (r) => setTestResult({ ok: r.status === 'connected', msg: r.status === 'connected' ? `Conectado a ${r.provider}` : (r.message ?? 'Error') }),
        onError: (e: any) => setTestResult({ ok: false, msg: e.response?.data?.message ?? 'No se pudo conectar' }),
    });

    const { data: landingTemplates, refetch: refetchLanding, isLoading: loadingLandingList } = useQuery<any[]>({
        queryKey: ['landing-templates'],
        queryFn: async () => {
            const res = await api.get('/landing');
            return res.data;
        },
        enabled: activeTab === 'landing'
    });

    const [editingLanding, setEditingLanding] = useState<any>(null);
    const [savingLanding, setSavingLanding] = useState(false);

    const DEFAULT_LANDING_CONFIG = {
        hero: {
            badge: 'Heladería & Comidas Rápidas',
            title: 'DUFI GELATO',
            subtitle: 'Sabores que enamoran',
            description: 'Gelatos artesanales y comidas rápidas preparadas con ingredientes seleccionados. Una experiencia única en cada bocado.',
            bg_image: ''
        },
        about: {
            title: 'Pasión por el sabor desde el primer día',
            body_1: 'En Dufi Gelato creemos que un buen helado puede transformar cualquier momento. Elaboramos nuestros gelatos de forma artesanal con ingredientes seleccionados, combinando sabores tradicionales con creaciones únicas que sorprenden a cada cliente.',
            body_2: 'Además de nuestras delicias heladas, ofrecemos comidas rápidas preparadas con el mismo amor y dedicación para que cada visita sea una experiencia completa e inolvidable.',
            bg_image: ''
        },
        features: [
            { icon: 'Star', text: 'Sabores Artesanales' },
            { icon: 'Leaf', text: 'Ingredientes Naturales' },
            { icon: 'Heart', text: 'Hecho con Amor' },
            { icon: 'Award', text: 'Calidad Premium' }
        ],
        contact: {
            phone: '+57 (XXX) XXX-XXXX',
            address: 'Tu dirección aquí, Ciudad — Colombia',
            instagram: '#',
            facebook: '#',
            whatsapp: '57XXXXXXXXXX',
            hours_weekdays: 'Lunes a Viernes: 10:00 am – 9:00 pm',
            hours_weekends: 'Sábados y Domingos: 10:00 am – 10:00 pm',
            map_iframe: ''
        }
    };

    const handleCreateLanding = () => {
        setEditingLanding({
            name: 'Nueva Plantilla',
            config: JSON.parse(JSON.stringify(DEFAULT_LANDING_CONFIG)),
            is_active: false
        });
    };

    const handleSaveLanding = async () => {
        if (!editingLanding.name.trim()) {
            toast.error('El nombre de la plantilla es obligatorio');
            return;
        }
        setSavingLanding(true);
        try {
            if (editingLanding.id) {
                await api.put(`/landing/${editingLanding.id}`, editingLanding);
                toast.success('Plantilla actualizada con éxito');
            } else {
                await api.post('/landing', editingLanding);
                toast.success('Plantilla creada con éxito');
            }
            setEditingLanding(null);
            refetchLanding();
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'Error al guardar la plantilla');
        } finally {
            setSavingLanding(false);
        }
    };

    const handleDeleteLanding = async (id: number) => {
        if (!window.confirm('¿Estás seguro de que deseas eliminar esta plantilla?')) return;
        try {
            await api.delete(`/landing/${id}`);
            toast.success('Plantilla eliminada');
            refetchLanding();
        } catch (err: any) {
            toast.error('Error al eliminar la plantilla');
        }
    };

    const handleActivateLanding = async (id: number) => {
        try {
            await api.post(`/landing/${id}/activate`);
            toast.success('Plantilla activada con éxito');
            refetchLanding();
        } catch (err: any) {
            toast.error('Error al activar la plantilla');
        }
    };

    const handleExportLanding = (template: any) => {
        const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(template.config, null, 2));
        const downloadAnchor = document.createElement('a');
        downloadAnchor.setAttribute("href", dataStr);
        downloadAnchor.setAttribute("download", `${template.name.replace(/\s+/g, '_')}_config.json`);
        document.body.appendChild(downloadAnchor);
        downloadAnchor.click();
        downloadAnchor.remove();
    };

    const handleImportLanding = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = async (event) => {
            try {
                const parsed = JSON.parse(event.target?.result as string);
                if (!parsed.hero || !parsed.about || !parsed.features) {
                    toast.error('El archivo JSON no tiene el formato válido de Landing Page');
                    return;
                }
                const name = file.name.replace('_config.json', '').replace('.json', '');
                await api.post('/landing', {
                    name: `Importado: ${name}`,
                    config: parsed,
                    is_active: false
                });
                toast.success('Configuración importada correctamente como nueva plantilla');
                refetchLanding();
            } catch (err) {
                toast.error('Error al parsear el archivo JSON');
            }
        };
        reader.readAsText(file);
    };

    const handleLandingImgUpload = (section: 'hero' | 'about', e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = ev => {
            setEditingLanding((prev: any) => {
                const next = { ...prev };
                next.config[section].bg_image = ev.target?.result as string;
                return { ...next };
            });
        };
        reader.readAsDataURL(file);
    };

    const tabs = [
        { id: 'general', label: 'General', icon: Building },
        { id: 'hardware', label: 'Hardware', icon: Printer },
        { id: 'payments', label: 'Pagos', icon: CreditCard },
        { id: 'promotions', label: 'Promociones & Happy Hour', icon: Tag },
        { id: 'ai', label: 'IA', icon: Sparkles },
        { id: 'dian', label: 'DIAN (Facturación)', icon: FileText },
        { id: 'landing', label: 'Landing Page', icon: Globe },
        ...(isAdmin ? [
            { id: 'cloud-sync', label: 'Sincronización Nube', icon: Cloud },
            { id: 'backups', label: 'Copias de Seguridad', icon: Database },
        ] : []),
        { id: 'account', label: 'Cuenta', icon: User },
    ];


    return (
        <div className="max-w-5xl mx-auto p-4 md:p-8 space-y-6">
            <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Configuración</h1>
                <p className="text-gray-500 dark:text-gray-400 mt-1">Administra las preferencias de tu sistema</p>
            </div>

            <div className="flex flex-col md:flex-row gap-6">
                {/* Sidebar */}
                <div className="w-full md:w-56 flex flex-col gap-1">
                    {tabs.map(({ id, label, icon: Icon }) => (
                        <button
                            key={id}
                            onClick={() => setActiveTab(id)}
                            className={cn(
                                'flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors font-medium text-sm',
                                activeTab === id
                                    ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-neutral-800'
                            )}
                        >
                            <Icon size={18} /> {label}
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div className="flex-1 bg-white dark:bg-neutral-800 rounded-xl shadow-sm border border-gray-100 dark:border-neutral-700 p-6 min-h-[500px]">

                    {/* ── GENERAL ── */}
                    {activeTab === 'general' && (
                        <div className="space-y-6 max-w-lg">
                            <h2 className="text-lg font-semibold">Información del Negocio</h2>

                            {/* Logo */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Logo del negocio
                                </label>
                                <div className="flex items-center gap-4">
                                    {form.logoUrl ? (
                                        <div className="relative">
                                            <img
                                                src={form.logoUrl}
                                                alt="Logo"
                                                className="h-16 w-auto max-w-[120px] object-contain rounded-lg border border-gray-200 dark:border-neutral-600 bg-white p-1"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => set('logoUrl', '')}
                                                className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600"
                                                aria-label="Quitar logo"
                                            >
                                                <X size={10} />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex h-16 w-24 items-center justify-center rounded-lg border-2 border-dashed border-gray-300 dark:border-neutral-600 bg-gray-50 dark:bg-neutral-700">
                                            <ImagePlus size={20} className="text-gray-400" />
                                        </div>
                                    )}
                                    <label className="cursor-pointer inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-neutral-600 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-neutral-700 transition">
                                        <ImagePlus size={14} />
                                        {form.logoUrl ? 'Cambiar logo' : 'Subir logo'}
                                        <input type="file" accept="image/*" onChange={handleLogoUpload} className="sr-only" />
                                    </label>
                                </div>
                                <p className="mt-1 text-xs text-gray-400">Aparecerá en la cabecera del ticket impreso.</p>
                            </div>

                            <div className="grid gap-4">
                                <Field id="name" label="Nombre del negocio" value={form.name} onChange={v => set('name', v)} placeholder="Mi Restaurante" />
                                <Field id="slogan" label="Eslogan (opcional)" value={form.slogan} onChange={v => set('slogan', v)} placeholder="El mejor sabor de la ciudad" />
                                <Field id="address" label="Dirección" value={form.address} onChange={v => set('address', v)} placeholder="Calle 123 # 45-67, Ciudad" />
                                <Field id="phone" label="Teléfono" value={form.phone} onChange={v => set('phone', v)} placeholder="(601) 123 4567" />
                                <Field id="nit" label="NIT / Identificación fiscal" value={form.nit} onChange={v => set('nit', v)} placeholder="900.123.456-7" />
                                <Field id="receiptFooter" label="Pie del ticket" value={form.receiptFooter} onChange={v => set('receiptFooter', v)} placeholder="¡Gracias por su visita!" multiline />
                            </div>

                            {/* Geolocalización para Pedidos QR */}
                            <div className="p-4 bg-gray-50 dark:bg-neutral-700/50 rounded-xl space-y-4 border border-gray-200/50 dark:border-neutral-600/30">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-semibold flex items-center gap-1.5">
                                            <MapPin size={16} className="text-blue-500" />
                                            Activar Validación GPS para Pedidos QR
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            Los clientes deben estar en el local para ordenar por QR.
                                        </p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={!!form.gpsEnabled}
                                            onChange={e => set('gpsEnabled', e.target.checked)}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
                                    </label>
                                </div>

                                {form.gpsEnabled && (
                                    <div className="grid gap-3 pt-3 border-t border-gray-200/60 dark:border-neutral-600/40">
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-1">Latitud</label>
                                                <input
                                                    type="text"
                                                    value={form.latitude || ''}
                                                    onChange={e => set('latitude', e.target.value)}
                                                    placeholder="Ej: 4.6097"
                                                    className="w-full px-3 py-1.5 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-mono"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-1">Longitud</label>
                                                <input
                                                    type="text"
                                                    value={form.longitude || ''}
                                                    onChange={e => set('longitude', e.target.value)}
                                                    placeholder="Ej: -74.0817"
                                                    className="w-full px-3 py-1.5 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-mono"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-1">Rango Máximo (metros)</label>
                                            <input
                                                type="number"
                                                value={form.maxDistance || '100'}
                                                onChange={e => set('maxDistance', e.target.value)}
                                                placeholder="Por defecto: 100"
                                                className="w-full px-3 py-1.5 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                            />
                                        </div>

                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (navigator.geolocation) {
                                                    navigator.geolocation.getCurrentPosition(
                                                        position => {
                                                            setForm(prev => ({
                                                                ...prev,
                                                                latitude: String(position.coords.latitude),
                                                                longitude: String(position.coords.longitude),
                                                            }));
                                                            toast.success('Coordenadas actuales obtenidas correctamente');
                                                        },
                                                        err => {
                                                            toast.error('Error al obtener ubicación. Asegúrate de dar permisos en tu navegador.');
                                                        }
                                                    );
                                                } else {
                                                    toast.error('Geolocalización no soportada en este navegador.');
                                                }
                                            }}
                                            className="w-full inline-flex items-center justify-center gap-1.5 py-2 px-3 bg-gray-200 dark:bg-neutral-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-neutral-600 rounded-lg text-xs font-semibold transition"
                                        >
                                            <Navigation size={13} /> Obtener coordenadas actuales
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Meta de Food Cost */}
                            <div className="p-4 bg-gray-50 dark:bg-neutral-700/40 rounded-lg border border-gray-100 dark:border-neutral-700">
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase mb-1">
                                    Meta de Food Cost (%) para KPIs y Reportes
                                </label>
                                <div className="flex items-center gap-2 max-w-xs">
                                    <input
                                        type="number"
                                        min="5"
                                        max="80"
                                        step="0.5"
                                        value={form.targetFoodCostPct ?? 30}
                                        onChange={e => set('targetFoodCostPct', Number(e.target.value))}
                                        placeholder="30"
                                        className="w-full px-3 py-1.5 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-semibold"
                                    />
                                    <span className="text-sm font-bold text-gray-500">%</span>
                                </div>
                                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1.5">
                                    Porcentaje objetivo del costo de materia prima sobre las ventas. El estándar del sector suele ubicarse entre 28% y 35%.
                                </p>
                            </div>

                            {/* Theme toggle */}
                            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-neutral-700/50 rounded-lg">
                                <div className="flex items-center gap-3">
                                    {darkMode ? <Moon size={18} className="text-purple-400" /> : <Sun size={18} className="text-amber-500" />}
                                    <div>
                                        <p className="text-sm font-medium">Modo Oscuro</p>
                                        <p className="text-xs text-gray-500">Cambia la apariencia</p>
                                    </div>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input type="checkbox" checked={darkMode} onChange={toggleTheme} className="sr-only peer" />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
                                </label>
                            </div>

                            <div className="pt-2 border-t border-gray-100 dark:border-neutral-700">
                                <button
                                    onClick={handleSaveGeneral}
                                    className="inline-flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm transition"
                                >
                                    {saved ? <CheckCircle2 size={16} /> : <Save size={16} />}
                                    {saved ? '¡Guardado!' : 'Guardar cambios'}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ── HARDWARE ── */}
                    {activeTab === 'hardware' && (
                        <div className="space-y-6 max-w-lg">
                            <h2 className="text-lg font-semibold">Hardware e Impresión</h2>
                            <div className="p-4 border border-blue-100 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800 rounded-lg text-blue-800 dark:text-blue-300 text-sm flex gap-3">
                                <Printer size={20} className="shrink-0 mt-0.5" />
                                <div>
                                    <p className="font-semibold">Impresoras Térmicas</p>
                                    <p className="mt-1">El sistema usa la impresión nativa del navegador. Elige el ancho de tu rollo para que el ticket salga perfecto.</p>
                                </div>
                            </div>

                            {/* Ancho del papel */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Ancho del papel (impresora térmica)
                                </label>
                                <div className="grid grid-cols-3 gap-2">
                                    {[
                                        { w: 58, label: '58 mm', desc: 'XP-58 y similares' },
                                        { w: 80, label: '80 mm', desc: 'POS estándar' },
                                    ].map(opt => (
                                        <button
                                            key={opt.w}
                                            type="button"
                                            onClick={() => setPaperWidth(opt.w)}
                                            className={cn(
                                                'rounded-xl border px-3 py-3 text-center transition',
                                                paperWidth === opt.w
                                                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-500'
                                                    : 'border-gray-200 dark:border-neutral-600 hover:border-blue-300',
                                            )}
                                        >
                                            <p className="font-bold text-sm">{opt.label}</p>
                                            <p className="text-[11px] text-gray-500">{opt.desc}</p>
                                        </button>
                                    ))}
                                    <div className="rounded-xl border border-gray-200 dark:border-neutral-600 px-3 py-2 text-center">
                                        <p className="text-[11px] text-gray-500 mb-1">Otro (mm)</p>
                                        <input
                                            type="number"
                                            min={40}
                                            max={120}
                                            value={paperWidth}
                                            onChange={e => setPaperWidth(Math.max(40, Math.min(120, Number(e.target.value) || 80)))}
                                            className="w-full rounded-md border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 px-2 py-1 text-center text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => { savePrinterConfig({ paperWidth }); toast.success(`Ancho de papel: ${paperWidth}mm guardado`); }}
                                    className="mt-3 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                                >
                                    <Save size={15} /> Guardar ancho
                                </button>
                                <p className="text-xs text-gray-500 mt-2">
                                    Tip: en el diálogo de impresión del navegador, elige tu impresora térmica y desactiva
                                    "encabezados y pies de página" y los márgenes para mejor resultado.
                                </p>
                            </div>
                            {/* ── Estado del Print Bridge ── */}
                            <div className="rounded-xl border border-gray-200 dark:border-neutral-700 p-4 space-y-2">
                                <div className="flex items-center justify-between">
                                    <p className="text-sm font-semibold flex items-center gap-2">
                                        <Activity size={15} className="text-blue-500" />
                                        Estado del Print Bridge
                                    </p>
                                    {bridgeStatus === null ? (
                                        <span className="text-xs text-gray-400">Cargando…</span>
                                    ) : bridgeStatus.bridge_active ? (
                                        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 px-2.5 py-0.5 text-xs font-semibold">
                                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                            Activo
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 px-2.5 py-0.5 text-xs font-semibold">
                                            <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                                            Inactivo
                                        </span>
                                    )}
                                </div>
                                {bridgeStatus !== null && (
                                    <div className="grid grid-cols-3 gap-2 text-center text-xs">
                                        <div className="rounded-lg bg-gray-50 dark:bg-neutral-700/50 py-2">
                                            <p className="font-bold text-lg text-blue-600">{bridgeStatus.pending}</p>
                                            <p className="text-gray-500">Pendientes</p>
                                        </div>
                                        <div className="rounded-lg bg-gray-50 dark:bg-neutral-700/50 py-2">
                                            <p className="font-bold text-lg text-amber-500">{bridgeStatus.printing}</p>
                                            <p className="text-gray-500">Imprimiendo</p>
                                        </div>
                                        <div className="rounded-lg bg-gray-50 dark:bg-neutral-700/50 py-2">
                                            <p className={`font-bold text-lg ${bridgeStatus.failed_last_24h > 0 ? 'text-red-500' : 'text-gray-400'}`}>{bridgeStatus.failed_last_24h}</p>
                                            <p className="text-gray-500">Fallidos 24h</p>
                                        </div>
                                    </div>
                                )}
                                {bridgeStatus && !bridgeStatus.bridge_active && (
                                    <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                                        <AlertTriangle size={12} />
                                        El bridge no está activo. Abre <code className="bg-gray-100 dark:bg-neutral-700 px-1 rounded">start.bat</code> en la PC del restaurante.
                                    </p>
                                )}
                                {bridgeStatus?.last_printed_at && (
                                    <p className="text-xs text-gray-400">
                                        Último impreso: {new Date(bridgeStatus.last_printed_at).toLocaleString()}
                                    </p>
                                )}
                            </div>

                            {/* ── Token de API para el Print Bridge ── */}
                            <BridgeTokenBox />

                            {/* Network printer config — stored in DB, shared by ALL devices */}
                            <div className="space-y-3">
                                <div className="rounded-lg border border-emerald-100 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-800 px-4 py-3 text-sm text-emerald-800 dark:text-emerald-300">
                                    <strong>📡 Config compartida en red.</strong> Esta configuración se guarda en el servidor y la usan automáticamente todos los dispositivos: PC, Android, iPhone, tablets. Sin setup por dispositivo.
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        IP de la impresora (ej: 192.168.1.100)
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="192.168.1.100"
                                        value={printerIp}
                                        onChange={e => { setPrinterIp(e.target.value); setPrinterTestResult(null); }}
                                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-mono"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Puerto TCP (normalmente 9100)
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="65535"
                                        value={printerPort}
                                        onChange={e => setPrinterPort(e.target.value)}
                                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                    />
                                </div>

                                {printerTestResult && (
                                    <div className={cn(
                                        'flex items-center gap-2 rounded-lg px-3 py-2 text-sm border',
                                        printerTestResult.ok
                                            ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                            : 'bg-red-50 border-red-200 text-red-700'
                                    )}>
                                        {printerTestResult.ok ? <Wifi size={14} /> : <WifiOff size={14} />}
                                        {printerTestResult.message}
                                    </div>
                                )}

                                <div className="flex gap-2 flex-wrap">
                                    <button
                                        type="button"
                                        disabled={savingPrinter}
                                        onClick={async () => {
                                            try {
                                                invalidatePrinterConfigCache();
                                                await savePrinterNet({
                                                    ip: printerIp,
                                                    port: parseInt(printerPort) || 9100,
                                                    paperWidth,
                                                    mode: 'network',
                                                });
                                                toast.success('✅ Config de impresora guardada en el servidor');
                                            } catch {
                                                toast.error('Error al guardar la configuración');
                                            }
                                        }}
                                        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                                    >
                                        {savingPrinter ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                                        Guardar en servidor
                                    </button>
                                    <button
                                        type="button"
                                        disabled={testingPrinter || !printerIp}
                                        onClick={async () => {
                                            setTestingPrinter(true);
                                            setPrinterTestResult(null);
                                            try {
                                                const res = await testPrinter(printerIp, parseInt(printerPort) || 9100);
                                                setPrinterTestResult(res);
                                            } catch {
                                                setPrinterTestResult({ ok: false, message: 'Error al probar la conexión' });
                                            } finally {
                                                setTestingPrinter(false);
                                            }
                                        }}
                                        className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                                    >
                                        {testingPrinter ? <Loader2 size={15} className="animate-spin" /> : <Plug size={15} />}
                                        Probar conexión
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500">
                                    La IP de la impresora se comparte automáticamente con todos los dispositivos.
                                    El Print Bridge local (PC del restaurante) la usa para imprimir.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* ── PAYMENTS ── */}
                    {activeTab === 'payments' && (
                        <div className="space-y-6">
                            <h2 className="text-lg font-semibold">Métodos de Pago</h2>
                            <p className="text-sm text-gray-500">Gestiona los métodos de pago aceptados.</p>
                            <button
                                onClick={() => navigate('/cash/methods')}
                                className="p-4 border rounded-xl hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition text-left group w-full max-w-sm"
                            >
                                <div className="flex items-center gap-3 mb-1">
                                    <div className="p-2 bg-emerald-100 text-emerald-600 rounded-lg">
                                        <CreditCard size={20} />
                                    </div>
                                    <span className="font-semibold text-sm">Configurar Métodos</span>
                                </div>
                                <p className="text-xs text-gray-500">Agregar o quitar métodos (Efectivo, Tarjeta, Transferencia).</p>
                            </button>
                        </div>
                    )}

                    {/* ── IA ── */}
                    {activeTab === 'ai' && (
                        <div className="space-y-6 max-w-lg">
                            <div>
                                <h2 className="text-lg font-semibold flex items-center gap-2">
                                    <Sparkles size={18} className="text-violet-500" /> Inteligencia Artificial
                                </h2>
                                <p className="text-sm text-gray-500 mt-1">
                                    Elige el proveedor de IA y configura tu API key. Los cambios se aplican al instante, sin reiniciar nada.
                                </p>
                            </div>

                            {/* Proveedor activo */}
                            {aiConfig && (
                                <div className="flex items-center gap-2 rounded-lg bg-violet-50 border border-violet-200 px-4 py-3 text-sm">
                                    <Sparkles size={14} className="text-violet-500 shrink-0" />
                                    <span className="text-violet-700">
                                        Proveedor activo: <strong>
                                            {aiConfig.providers.find(p => p.id === aiConfig.active)?.name ?? aiConfig.active}
                                        </strong>
                                    </span>
                                </div>
                            )}

                            {/* Selector de proveedor */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Proveedor</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {(aiConfig?.providers ?? []).map(p => (
                                        <button
                                            key={p.id}
                                            type="button"
                                            onClick={() => { setSelectedProvider(p.id); setModelInput(p.model); setApiKeyInput(''); setTestResult(null); }}
                                            className={cn(
                                                'rounded-xl border px-3 py-3 text-center text-sm font-medium transition relative',
                                                selectedProvider === p.id
                                                    ? 'border-violet-400 bg-violet-50 text-violet-700'
                                                    : 'border-gray-200 bg-white text-gray-600 hover:border-violet-200',
                                            )}
                                        >
                                            {p.name.split(' ')[0]}
                                            {p.configured && (
                                                <CheckCircle2 size={13} className="absolute top-1.5 right-1.5 text-emerald-500" />
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {selectedInfo && (
                                <>
                                    {/* API Key */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            API Key de {selectedInfo.name}
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showKey ? 'text' : 'password'}
                                                value={apiKeyInput}
                                                onChange={e => setApiKeyInput(e.target.value)}
                                                placeholder={selectedInfo.configured ? `Configurada: ${selectedInfo.keyMasked} (deja vacío para mantener)` : 'Pega tu API key aquí'}
                                                className="w-full px-3 py-2 pr-10 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm"
                                            />
                                            <button type="button" onClick={() => setShowKey(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                                {showKey ? <EyeOff size={15} /> : <Eye size={15} />}
                                            </button>
                                        </div>
                                        <a href={selectedInfo.keyUrl} target="_blank" rel="noopener noreferrer"
                                           className="inline-flex items-center gap-1 text-violet-600 hover:underline text-xs font-medium mt-1.5">
                                            Obtener API Key <ExternalLink size={10} />
                                        </a>
                                    </div>

                                    {/* Modelo */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Modelo <span className="text-gray-400 font-normal">(opcional)</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={modelInput}
                                            onChange={e => setModelInput(e.target.value)}
                                            placeholder={selectedInfo.model}
                                            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm"
                                        />
                                    </div>

                                    {/* Resultado del test */}
                                    {testResult && (
                                        <div className={cn(
                                            'rounded-lg px-4 py-2.5 text-sm border',
                                            testResult.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-red-50 border-red-200 text-red-700'
                                        )}>
                                            {testResult.ok ? '✓ ' : '✕ '}{testResult.msg}
                                        </div>
                                    )}

                                    {/* Acciones */}
                                    <div className="flex gap-3">
                                        <button
                                            type="button"
                                            onClick={() => saveAiMutation.mutate()}
                                            disabled={saveAiMutation.isPending}
                                            className="flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-60"
                                        >
                                            {saveAiMutation.isPending ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                                            Guardar y activar
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => testAiMutation.mutate()}
                                            disabled={testAiMutation.isPending}
                                            className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                                        >
                                            {testAiMutation.isPending ? <Loader2 size={15} className="animate-spin" /> : <Plug size={15} />}
                                            Probar conexión
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* ── DIAN ── */}
                    {activeTab === 'dian' && (
                        <div className="space-y-6">
                            <div>
                                <h2 className="text-lg font-semibold flex items-center gap-2">
                                    <FileText size={18} className="text-blue-500" /> Facturación Electrónica DIAN
                                </h2>
                                <p className="text-sm text-gray-500 mt-1">
                                    Configure los parámetros requeridos para la emisión de facturas electrónicas válidas ante la DIAN (Colombia).
                                </p>
                            </div>

                            {loadingDian ? (
                                <div className="flex justify-center items-center py-12">
                                    <Loader2 className="animate-spin text-blue-500" size={24} />
                                    <span className="ml-2 text-sm text-muted">Cargando configuración DIAN...</span>
                                </div>
                            ) : (
                                <form onSubmit={handleSaveDian} className="space-y-6 max-w-xl">
                                    {/* Habilitar / Modo Simulación */}
                                    <div className="p-4 bg-gray-50 dark:bg-neutral-700/50 rounded-xl space-y-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm font-semibold">Habilitar Facturación Electrónica</p>
                                                <p className="text-xs text-gray-500">Permite emitir facturas oficiales al cobrar órdenes.</p>
                                            </div>
                                            <input
                                                type="checkbox"
                                                checked={dianForm.enabled}
                                                onChange={e => setDianForm(prev => ({ ...prev, enabled: e.target.checked }))}
                                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                        </div>

                                        <div className="flex items-center justify-between border-t border-gray-200 dark:border-neutral-700 pt-3">
                                            <div>
                                                <p className="text-sm font-semibold">Modo Simulación / Mock (Sandbox)</p>
                                                <p className="text-xs text-gray-500">Simula respuestas exitosas de la DIAN sin consumir tokens ni certs.</p>
                                            </div>
                                            <input
                                                type="checkbox"
                                                checked={dianForm.simulation}
                                                onChange={e => setDianForm(prev => ({ ...prev, simulation: e.target.checked }))}
                                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                        </div>
                                    </div>

                                    {/* Credenciales del API Provider */}
                                    <div className="space-y-4">
                                        <h3 className="text-sm font-bold border-b pb-1">Proveedor Tecnológico (API)</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-semibold text-muted uppercase mb-1">API URL Endpoint</label>
                                                <input
                                                    type="text"
                                                    value={dianForm.api_url}
                                                    onChange={e => setDianForm(prev => ({ ...prev, api_url: e.target.value }))}
                                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                                    placeholder="Ej: https://api-sandbox.factus.co"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-muted uppercase mb-1">Token de API</label>
                                                <input
                                                    type="password"
                                                    value={dianForm.api_token}
                                                    onChange={e => setDianForm(prev => ({ ...prev, api_token: e.target.value }))}
                                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                                    placeholder="Pega tu Bearer Token aquí"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Impuesto predeterminado */}
                                    <div className="space-y-4">
                                        <h3 className="text-sm font-bold border-b pb-1">Impuestos y Tasas</h3>
                                        <div>
                                            <label className="block text-xs font-semibold text-muted uppercase mb-1">Impuesto por Defecto para Productos</label>
                                            <select
                                                value={dianForm.default_tax}
                                                onChange={e => setDianForm(prev => ({ ...prev, default_tax: e.target.value }))}
                                                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                            >
                                                <option value="EXENTO">Exento / Sin Impuesto (No Responsable IVA)</option>
                                                <option value="INC8">Impuesto Nacional al Consumo (INC 8%)</option>
                                                <option value="IVA19">IVA General (19%)</option>
                                            </select>
                                            <p className="text-[11px] text-gray-400 mt-1">Este impuesto se calculará de manera global para los ítems al emitir la factura.</p>
                                        </div>
                                    </div>

                                    {/* Resolución DIAN */}
                                    <div className="space-y-4">
                                        <h3 className="text-sm font-bold border-b pb-1">Resolución de Facturación</h3>
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                            <div>
                                                <label className="block text-xs font-semibold text-muted uppercase mb-1">Prefijo</label>
                                                <input
                                                    type="text"
                                                    value={dianForm.resolution_prefix}
                                                    onChange={e => setDianForm(prev => ({ ...prev, resolution_prefix: e.target.value }))}
                                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                                    placeholder="Ej: SETT o FE"
                                                />
                                            </div>
                                            <div className="col-span-2">
                                                <label className="block text-xs font-semibold text-muted uppercase mb-1">Número de Resolución</label>
                                                <input
                                                    type="text"
                                                    value={dianForm.resolution_number}
                                                    onChange={e => setDianForm(prev => ({ ...prev, resolution_number: e.target.value }))}
                                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                                    placeholder="Ej: 18760000001"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-muted uppercase mb-1">Rango Desde</label>
                                                <input
                                                    type="text"
                                                    value={dianForm.resolution_start}
                                                    onChange={e => setDianForm(prev => ({ ...prev, resolution_start: e.target.value }))}
                                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-muted uppercase mb-1">Rango Hasta</label>
                                                <input
                                                    type="text"
                                                    value={dianForm.resolution_end}
                                                    onChange={e => setDianForm(prev => ({ ...prev, resolution_end: e.target.value }))}
                                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                                />
                                            </div>
                                            <div className="col-span-2 md:col-span-1">
                                                <label className="block text-xs font-semibold text-muted uppercase mb-1">Fecha de Resolución</label>
                                                <input
                                                    type="date"
                                                    value={dianForm.resolution_date}
                                                    onChange={e => setDianForm(prev => ({ ...prev, resolution_date: e.target.value }))}
                                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Datos del Emisor */}
                                    <div className="space-y-4">
                                        <h3 className="text-sm font-bold border-b pb-1">Datos del Emisor (Tu Negocio)</h3>
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                            <div className="col-span-2">
                                                <label className="block text-xs font-semibold text-muted uppercase mb-1">NIT del Negocio</label>
                                                <input
                                                    type="text"
                                                    value={dianForm.issuer_nit}
                                                    onChange={e => setDianForm(prev => ({ ...prev, issuer_nit: e.target.value }))}
                                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                                    placeholder="Ej: 901345678"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-muted uppercase mb-1">D.V.</label>
                                                <input
                                                    type="text"
                                                    maxLength={1}
                                                    value={dianForm.issuer_dv}
                                                    onChange={e => setDianForm(prev => ({ ...prev, issuer_dv: e.target.value }))}
                                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                                    placeholder="Ej: 9"
                                                />
                                            </div>
                                            <div className="col-span-2">
                                                <label className="block text-xs font-semibold text-muted uppercase mb-1">Nombre Legal / Razón Social</label>
                                                <input
                                                    type="text"
                                                    value={dianForm.issuer_name}
                                                    onChange={e => setDianForm(prev => ({ ...prev, issuer_name: e.target.value }))}
                                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                                    placeholder="Ej: Gelatería Dufi S.A.S."
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-muted uppercase mb-1">Responsabilidad Fiscal</label>
                                                <select
                                                    value={dianForm.issuer_regime}
                                                    onChange={e => setDianForm(prev => ({ ...prev, issuer_regime: e.target.value }))}
                                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                                >
                                                    <option value="O-99">No responsable de IVA</option>
                                                    <option value="O-47">Régimen Simple de Tributación</option>
                                                    <option value="O-13">Responsable de IVA</option>
                                                </select>
                                            </div>
                                            <div className="col-span-2">
                                                <label className="block text-xs font-semibold text-muted uppercase mb-1">Dirección Fiscal</label>
                                                <input
                                                    type="text"
                                                    value={dianForm.issuer_address}
                                                    onChange={e => setDianForm(prev => ({ ...prev, issuer_address: e.target.value }))}
                                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                                    placeholder="Ej: Carrera 15 # 82-44"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-muted uppercase mb-1">Cod. Divipola Ciudad</label>
                                                <input
                                                    type="text"
                                                    value={dianForm.issuer_city_code}
                                                    onChange={e => setDianForm(prev => ({ ...prev, issuer_city_code: e.target.value }))}
                                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                                    placeholder="Ej: 11001 (Bogotá)"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Botón Guardar */}
                                    <div className="pt-2">
                                        <button
                                            type="submit"
                                            disabled={savingDian}
                                            className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60 transition"
                                        >
                                            {savingDian ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                                            Guardar Configuración DIAN
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    )}

                    {/* ── LANDING PAGE ── */}
                    {activeTab === 'landing' && (
                        <div className="space-y-6">
                            <div className="flex items-center justify-between">
                                <h2 className="text-lg font-semibold">Diseños de Landing Page</h2>
                                {!editingLanding && (
                                    <div className="flex gap-2">
                                        <label className="cursor-pointer flex items-center gap-1.5 rounded-lg border border-gray-300 dark:border-neutral-600 px-3 py-1.5 text-xs font-semibold hover:bg-gray-50 dark:hover:bg-neutral-700 transition">
                                            <Upload size={14} /> Importar JSON
                                            <input type="file" accept=".json" onChange={handleImportLanding} className="sr-only" />
                                        </label>
                                        <button
                                            onClick={handleCreateLanding}
                                            className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 transition"
                                        >
                                            <Plus size={14} /> Nuevo Diseño
                                        </button>
                                    </div>
                                )}
                            </div>

                            {editingLanding ? (
                                <div className="space-y-6">
                                    <div className="flex items-center justify-between border-b pb-3">
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="text"
                                                value={editingLanding.name}
                                                onChange={e => setEditingLanding((prev: any) => ({ ...prev, name: e.target.value }))}
                                                className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="Nombre del diseño"
                                            />
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => setEditingLanding(null)}
                                                className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold hover:bg-gray-50 dark:bg-neutral-700 dark:border-neutral-600 transition"
                                            >
                                                Volver
                                            </button>
                                            <button
                                                onClick={handleSaveLanding}
                                                disabled={savingLanding}
                                                className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-60 transition"
                                            >
                                                {savingLanding ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                                                Guardar Plantilla
                                            </button>
                                        </div>
                                    </div>

                                    <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
                                        {/* HERO */}
                                        <div className="space-y-4 border-b pb-4">
                                            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">Sección Principal (Hero)</h3>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <Field
                                                    label="Etiqueta / Badge"
                                                    id="hero-badge"
                                                    value={editingLanding.config.hero.badge}
                                                    onChange={v => setEditingLanding((prev: any) => {
                                                        const next = { ...prev };
                                                        next.config.hero.badge = v;
                                                        return { ...next };
                                                    })}
                                                />
                                                <Field
                                                    label="Título Principal"
                                                    id="hero-title"
                                                    value={editingLanding.config.hero.title}
                                                    onChange={v => setEditingLanding((prev: any) => {
                                                        const next = { ...prev };
                                                        next.config.hero.title = v;
                                                        return { ...next };
                                                    })}
                                                />
                                                <Field
                                                    label="Subtítulo"
                                                    id="hero-subtitle"
                                                    value={editingLanding.config.hero.subtitle}
                                                    onChange={v => setEditingLanding((prev: any) => {
                                                        const next = { ...prev };
                                                        next.config.hero.subtitle = v;
                                                        return { ...next };
                                                    })}
                                                />
                                                <div className="md:col-span-2">
                                                    <Field
                                                        label="Descripción"
                                                        id="hero-desc"
                                                        value={editingLanding.config.hero.description}
                                                        onChange={v => setEditingLanding((prev: any) => {
                                                            const next = { ...prev };
                                                            next.config.hero.description = v;
                                                            return { ...next };
                                                        })}
                                                        multiline
                                                    />
                                                </div>
                                                <div className="md:col-span-2">
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Imagen de fondo del Hero</label>
                                                    <div className="flex items-center gap-4">
                                                        {editingLanding.config.hero.bg_image ? (
                                                            <div className="relative">
                                                                <img src={editingLanding.config.hero.bg_image} alt="Hero bg" className="h-16 w-24 object-cover rounded-lg border bg-white p-1" />
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setEditingLanding((prev: any) => {
                                                                        const next = { ...prev };
                                                                        next.config.hero.bg_image = '';
                                                                        return { ...next };
                                                                    })}
                                                                    className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white"
                                                                >
                                                                    <X size={10} />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <div className="h-16 w-24 border-2 border-dashed rounded-lg bg-gray-50 flex items-center justify-center dark:bg-neutral-700"><ImagePlus size={20} className="text-gray-400" /></div>
                                                        )}
                                                        <label className="cursor-pointer inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-neutral-700 transition">
                                                            <ImagePlus size={14} /> Subir Imagen
                                                            <input type="file" accept="image/*" onChange={e => handleLandingImgUpload('hero', e)} className="sr-only" />
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* ABOUT */}
                                        <div className="space-y-4 border-b pb-4">
                                            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">Sección Nosotros</h3>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div className="md:col-span-2">
                                                    <Field
                                                        label="Título de Sección"
                                                        id="about-title"
                                                        value={editingLanding.config.about.title}
                                                        onChange={v => setEditingLanding((prev: any) => {
                                                            const next = { ...prev };
                                                            next.config.about.title = v;
                                                            return { ...next };
                                                        })}
                                                    />
                                                </div>
                                                <div className="md:col-span-2">
                                                    <Field
                                                        label="Primer Párrafo"
                                                        id="about-body-1"
                                                        value={editingLanding.config.about.body_1}
                                                        onChange={v => setEditingLanding((prev: any) => {
                                                            const next = { ...prev };
                                                            next.config.about.body_1 = v;
                                                            return { ...next };
                                                        })}
                                                        multiline
                                                    />
                                                </div>
                                                <div className="md:col-span-2">
                                                    <Field
                                                        label="Segundo Párrafo"
                                                        id="about-body-2"
                                                        value={editingLanding.config.about.body_2}
                                                        onChange={v => setEditingLanding((prev: any) => {
                                                            const next = { ...prev };
                                                            next.config.about.body_2 = v;
                                                            return { ...next };
                                                        })}
                                                        multiline
                                                    />
                                                </div>
                                                <div className="md:col-span-2">
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Imagen de la Sección</label>
                                                    <div className="flex items-center gap-4">
                                                        {editingLanding.config.about.bg_image ? (
                                                            <div className="relative">
                                                                <img src={editingLanding.config.about.bg_image} alt="About bg" className="h-16 w-24 object-cover rounded-lg border bg-white p-1" />
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setEditingLanding((prev: any) => {
                                                                        const next = { ...prev };
                                                                        next.config.about.bg_image = '';
                                                                        return { ...next };
                                                                    })}
                                                                    className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white"
                                                                >
                                                                    <X size={10} />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <div className="h-16 w-24 border-2 border-dashed rounded-lg bg-gray-50 flex items-center justify-center dark:bg-neutral-700"><ImagePlus size={20} className="text-gray-400" /></div>
                                                        )}
                                                        <label className="cursor-pointer inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-neutral-700 transition">
                                                            <ImagePlus size={14} /> Subir Imagen
                                                            <input type="file" accept="image/*" onChange={e => handleLandingImgUpload('about', e)} className="sr-only" />
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* FEATURES */}
                                        <div className="space-y-4 border-b pb-4">
                                            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">Características Destacadas (Máximo 4)</h3>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {editingLanding.config.features.map((f: any, idx: number) => (
                                                    <div key={idx} className="p-4 border rounded-xl bg-gray-50 dark:bg-neutral-700/50 space-y-2">
                                                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500">Característica {idx + 1}</label>
                                                        <div>
                                                            <label className="block text-xs text-gray-500 mb-1">Icono</label>
                                                            <select
                                                                value={f.icon}
                                                                onChange={e => setEditingLanding((prev: any) => {
                                                                    const next = { ...prev };
                                                                    next.config.features[idx].icon = e.target.value;
                                                                    return { ...next };
                                                                })}
                                                                className="w-full px-3 py-1.5 rounded-lg border text-sm bg-white dark:bg-neutral-750"
                                                            >
                                                                <option value="Star">Estrella (Star)</option>
                                                                <option value="Leaf">Hoja (Leaf)</option>
                                                                <option value="Heart">Corazón (Heart)</option>
                                                                <option value="Award">Medalla (Award)</option>
                                                                <option value="IceCream">Helado (IceCream)</option>
                                                                <option value="Clock">Reloj (Clock)</option>
                                                                <option value="MapPin">Pin Mapa (MapPin)</option>
                                                                <option value="Phone">Teléfono (Phone)</option>
                                                                <option value="MessageCircle">WhatsApp (MessageCircle)</option>
                                                            </select>
                                                        </div>
                                                        <Field
                                                            label="Texto"
                                                            id={`feature-text-${idx}`}
                                                            value={f.text}
                                                            onChange={v => setEditingLanding((prev: any) => {
                                                                const next = { ...prev };
                                                                next.config.features[idx].text = v;
                                                                return { ...next };
                                                            })}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* CONTACT & SOCIAL */}
                                        <div className="space-y-4 pb-4">
                                            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">Contacto y Redes Sociales</h3>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <Field
                                                    label="Teléfono"
                                                    id="contact-phone"
                                                    value={editingLanding.config.contact.phone}
                                                    onChange={v => setEditingLanding((prev: any) => {
                                                        const next = { ...prev };
                                                        next.config.contact.phone = v;
                                                        return { ...next };
                                                    })}
                                                />
                                                <Field
                                                    label="Dirección"
                                                    id="contact-address"
                                                    value={editingLanding.config.contact.address}
                                                    onChange={v => setEditingLanding((prev: any) => {
                                                        const next = { ...prev };
                                                        next.config.contact.address = v;
                                                        return { ...next };
                                                    })}
                                                />
                                                <Field
                                                    label="Instagram Link"
                                                    id="contact-instagram"
                                                    value={editingLanding.config.contact.instagram}
                                                    onChange={v => setEditingLanding((prev: any) => {
                                                        const next = { ...prev };
                                                        next.config.contact.instagram = v;
                                                        return { ...next };
                                                    })}
                                                />
                                                <Field
                                                    label="Facebook Link"
                                                    id="contact-facebook"
                                                    value={editingLanding.config.contact.facebook}
                                                    onChange={v => setEditingLanding((prev: any) => {
                                                        const next = { ...prev };
                                                        next.config.contact.facebook = v;
                                                        return { ...next };
                                                    })}
                                                />
                                                <Field
                                                    label="WhatsApp Número (Sin + ni espacios)"
                                                    id="contact-whatsapp"
                                                    value={editingLanding.config.contact.whatsapp}
                                                    onChange={v => setEditingLanding((prev: any) => {
                                                        const next = { ...prev };
                                                        next.config.contact.whatsapp = v;
                                                        return { ...next };
                                                    })}
                                                />
                                                <Field
                                                    label="Horario (Lunes a Viernes)"
                                                    id="contact-hours-weekdays"
                                                    value={editingLanding.config.contact.hours_weekdays || ''}
                                                    onChange={v => setEditingLanding((prev: any) => {
                                                        const next = { ...prev };
                                                        next.config.contact.hours_weekdays = v;
                                                        return { ...next };
                                                    })}
                                                />
                                                <Field
                                                    label="Horario (Sábados y Domingos)"
                                                    id="contact-hours-weekends"
                                                    value={editingLanding.config.contact.hours_weekends || ''}
                                                    onChange={v => setEditingLanding((prev: any) => {
                                                        const next = { ...prev };
                                                        next.config.contact.hours_weekends = v;
                                                        return { ...next };
                                                    })}
                                                />
                                                <Field
                                                    label="URL Mapa Embed (Google Maps Iframe src)"
                                                    id="contact-map-iframe"
                                                    value={editingLanding.config.contact.map_iframe || ''}
                                                    onChange={v => setEditingLanding((prev: any) => {
                                                        const next = { ...prev };
                                                        next.config.contact.map_iframe = v;
                                                        return { ...next };
                                                    })}
                                                />
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {loadingLandingList ? (
                                        <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-blue-500" /></div>
                                    ) : landingTemplates && landingTemplates.length > 0 ? (
                                        <div className="divide-y divide-gray-150 border rounded-xl bg-white dark:bg-neutral-800 dark:border-neutral-700">
                                            {landingTemplates.map((template: any) => (
                                                <div key={template.id} className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-neutral-750">
                                                    <div>
                                                        <p className="font-semibold text-sm">{template.name}</p>
                                                        <span className={`inline-block rounded px-2 py-0.5 mt-1 text-xs font-semibold ${template.is_active ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-gray-100 text-gray-600 dark:bg-neutral-700 dark:text-neutral-350'}`}>
                                                            {template.is_active ? 'Activa' : 'Inactiva'}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {!template.is_active && (
                                                            <button
                                                                onClick={() => handleActivateLanding(template.id)}
                                                                className="px-2.5 py-1 text-xs font-semibold border rounded-lg hover:bg-gray-50 dark:hover:bg-neutral-700 transition"
                                                            >
                                                                Activar
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => setEditingLanding(JSON.parse(JSON.stringify(template)))}
                                                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-gray-100 dark:hover:bg-neutral-700 rounded-lg transition"
                                                            title="Editar"
                                                        >
                                                            <Edit3 size={15} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleExportLanding(template)}
                                                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-gray-100 dark:hover:bg-neutral-700 rounded-lg transition"
                                                            title="Exportar"
                                                        >
                                                            <Download size={15} />
                                                        </button>
                                                        {!template.is_active && (
                                                            <button
                                                                onClick={() => handleDeleteLanding(template.id)}
                                                                className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-950 rounded-lg transition"
                                                                title="Eliminar"
                                                            >
                                                                <Trash2 size={15} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center justify-center py-12 border border-dashed rounded-xl bg-gray-50 dark:bg-neutral-800 dark:border-neutral-750">
                                            <p className="text-sm text-gray-500">No hay diseños configurados</p>
                                            <button
                                                onClick={handleCreateLanding}
                                                className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3.5 py-2 text-xs font-semibold text-white hover:bg-blue-700 transition"
                                            >
                                                <Plus size={14} /> Crear primer diseño
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── BACKUPS (SOLO ADMIN) ── */}
                    {activeTab === 'backups' && isAdmin && (
                        <BackupManager embedded />
                    )}

                    {/* ── SINCRONIZACIÓN NUBE (SOLO ADMIN) ── */}
                    {activeTab === 'cloud-sync' && isAdmin && (
                        <CloudSyncManager embedded />
                    )}

                    {/* ── PROMOCIONES & HAPPY HOUR ── */}
                    {activeTab === 'promotions' && (
                        <PromotionsManager />
                    )}

                    {/* ── ACCOUNT ── */}
                    {activeTab === 'account' && (
                        <div className="space-y-6">
                            <h2 className="text-lg font-semibold">Mi Cuenta</h2>
                            <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-neutral-700/50 rounded-xl">
                                <div className="p-3 bg-blue-100 text-blue-600 rounded-full">
                                    <User size={28} />
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-semibold text-sm">Perfil de Usuario</h3>
                                    <p className="text-xs text-gray-500">Gestiona tu información personal y contraseña.</p>
                                </div>
                                <button
                                    onClick={() => navigate('/profile')}
                                    className="px-4 py-2 text-sm bg-white border border-gray-200 shadow-sm rounded-lg hover:bg-gray-50 dark:bg-neutral-700 dark:border-neutral-600"
                                >
                                    Ir al Perfil
                                </button>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
}
